import { extractCellText } from "../../table/utils/extract-cell-text";

const WORDS_PER_MINUTE = 200;
// Skim/scroll overhead per slide; also covers non-text elements (images, buttons, embeds).
const SECONDS_PER_SLIDE = 7;

type ReadingTimeNode = {
  type?: string;
  settings?: Record<string, unknown>;
  children?: unknown;
};

const countWords = (html: string): number => {
  const text = extractCellText(html);
  return text ? text.split(/\s+/).length : 0;
};

/**
 * Works on both entity shapes: the reader's nested `story.slides` trees
 * (recurses via `children`) and the editor's flat `flatContentLayers` values
 * (no `children` arrays — every entity is already in `nodes`).
 */
export const estimateReadingTimeSeconds = ({
  nodes,
  slideCount,
}: {
  nodes: unknown[];
  slideCount: number;
}): number => {
  let words = 0;

  const walk = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    const node = value as ReadingTimeNode;
    const { settings } = node;

    if (settings?.name === "RichText" && typeof settings.content === "string") {
      words += countWords(settings.content);
    }

    if (settings?.name === "Table" && Array.isArray(settings.tableData)) {
      for (const cell of settings.tableData) {
        const content = (cell as { content?: unknown } | null)?.content;
        if (typeof content === "string") words += countWords(content);
      }
    }

    if (Array.isArray(node.children)) node.children.forEach(walk);
  };

  nodes.forEach(walk);

  return Math.round((words / WORDS_PER_MINUTE) * 60 + slideCount * SECONDS_PER_SLIDE);
};

export const formatReadingTime = (seconds: number): string => {
  if (seconds < 60) return "< 1 min";
  return `${Math.round(seconds / 60)} min`;
};
